import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, FileText, Loader2, Sparkles } from "lucide-react";

import { extractFromDocument, extractFromText } from "@/api/client";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { VoiceDictationButton } from "@/components/VoiceDictationButton";
import { useDebouncedCallback } from "@/lib/useDebouncedCallback";
import { useExtractionStore, type ExtractedField } from "./useExtractionStore";
import type { ProjectDataFormApi } from "./ProjectDataForm";

/**
 * "Opis zahvata" — typed or dictated, it flows (debounced) into the
 * draft's ``zahvat_opis`` (the AI context of the generate request) and
 * auto-runs the NL extraction so a sharpened description refreshes the
 * pre-filled fields (DXF/IFC values still win, as everywhere).
 *
 * Lives in the ENTRY ZONE at the top of Donesi, right under the basket
 * (Lukin feedback 15.07.: free text is the fastest way in — one text
 * fills many fields AND becomes the opis section). The console's "Opis
 * zahvata" row shows the status and focuses this editor. With
 * ``documentUpload`` it also accepts a projektni zadatak (PDF/DOCX) whose
 * text is field-extracted through ``/api/nl/extract-from-document``.
 */
export function OpisZahvataInline({
  initialText,
  formApi,
  documentUpload,
}: {
  initialText: string;
  formApi: MutableRefObject<ProjectDataFormApi | null>;
  /** Show the "priloži projektni zadatak" (PDF/DOCX) action. */
  documentUpload?: boolean;
}) {
  const [text, setText] = useState(initialText);
  // Text whose fields we've already pulled — guards the auto-extract loop
  // so it fires once per meaningful edit, not on every keystroke.
  const lastExtracted = useRef<string>("");

  const pushToForm = useDebouncedCallback((value: string) => {
    formApi.current?.setZahvatOpis(value);
  }, 500);

  const extract = useMutation({
    mutationFn: async () => {
      const current = useExtractionStore.getState().confidence;
      return (await extractFromText(text, current)) as {
        extracted?: Record<string, ExtractedField>;
        warnings?: string[];
      };
    },
    onSuccess: (raw) => {
      formApi.current?.applyNlExtraction(raw.extracted ?? {}, raw.warnings ?? []);
    },
  });

  const hasText = text.trim().length > 0;

  // Auto-extract: once the description settles (typed or dictated), pull
  // fields without a manual click. Debounced so we make ONE call per pause,
  // guarded by ``lastExtracted`` so an unchanged description never re-fires.
  // The manual button below stays as an explicit "try again".
  useEffect(() => {
    const trimmed = text.trim();
    if (trimmed.length < 12) return; // too short to carry real fields
    if (trimmed === lastExtracted.current) return;
    if (extract.isPending) return;
    const timer = window.setTimeout(() => {
      lastExtracted.current = trimmed;
      extract.mutate();
    }, 1600);
    return () => window.clearTimeout(timer);
    // ``extract`` identity is stable enough for this debounce; we only
    // re-arm on text changes and pending transitions.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [text, extract.isPending]);

  return (
    <div className="py-1.5">
      <div className="mb-1.5 flex items-center justify-end gap-2">
        {documentUpload && <ProjektniZadatakButton formApi={formApi} />}
        <VoiceDictationButton
          disabled={extract.isPending}
          onTranscript={(t) =>
            setText((d) => {
              const next = d.trim() ? `${d.trim()} ${t}` : t;
              pushToForm(next);
              return next;
            })
          }
        />
      </div>
      <Textarea
        rows={4}
        className="w-full"
        value={text}
        disabled={extract.isPending}
        onChange={(e) => {
          setText(e.target.value);
          pushToForm(e.target.value);
        }}
        placeholder="Npr. „Novogradnja obiteljske kuće, k.č. 706/1, Po+P+1, dvije parkirne pozicije…“"
      />
      <div className="mt-2 flex flex-wrap items-center justify-between gap-3">
        <span className="flex items-center gap-1.5 text-[0.76rem] text-stone-500">
          {extract.isPending ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin text-ind-600" />
              Čitam opis i punim polja…
            </>
          ) : extract.isSuccess ? (
            <>
              <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
              Polja popunjena iz opisa — provjeri ih na koraku Provjeri.
            </>
          ) : (
            "Polja se automatski popune iz opisa; tekst ide AI-ju kao kontekst."
          )}
        </span>
        <Button
          type="button"
          variant="outline"
          size="sm"
          disabled={!hasText || extract.isPending}
          onClick={() => {
            lastExtracted.current = ""; // force a re-run on the same text
            extract.mutate();
          }}
        >
          {extract.isPending ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Sparkles className="h-3.5 w-3.5" />
          )}
          Osvježi iz opisa
        </Button>
      </div>
      {extract.error instanceof Error && (
        <p className="mt-2 text-[0.8rem] text-red-700">
          Greška: {extract.error.message}
        </p>
      )}
    </div>
  );
}

/**
 * "Priloži projektni zadatak" — a PDF/DOCX brief whose text the NL
 * extractor reads into form fields (the path the old "DOKUMENT" tab
 * carried; Luka asked for it back, 15.07.). Fields land through the same
 * ``applyNlExtraction`` sink as the typed opis — DXF/IFC values still win.
 */
function ProjektniZadatakButton({
  formApi,
}: {
  formApi: MutableRefObject<ProjectDataFormApi | null>;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const current = useExtractionStore.getState().confidence;
      return (await extractFromDocument(file, current)) as {
        extracted?: Record<string, ExtractedField>;
        warnings?: string[];
      };
    },
    onSuccess: (raw) => {
      formApi.current?.applyNlExtraction(raw.extracted ?? {}, raw.warnings ?? []);
    },
  });

  const extractedCount = upload.isSuccess
    ? Object.values(upload.data?.extracted ?? {}).filter(
        (f) => f && f.value != null && f.value !== "",
      ).length
    : 0;

  return (
    <span className="inline-flex items-center gap-2">
      <input
        ref={inputRef}
        type="file"
        accept=".pdf,.docx"
        className="hidden"
        onChange={(e) => {
          const file = e.target.files?.[0];
          if (file) {
            setFileName(file.name);
            upload.mutate(file);
          }
          e.target.value = "";
        }}
      />
      {upload.isSuccess && (
        <span className="inline-flex items-center gap-1 text-[0.74rem] text-green-700">
          <CheckCircle2 className="h-3 w-3" />
          {extractedCount > 0
            ? `${fileName}: ${extractedCount} ${extractedCount === 1 ? "polje" : "polja"} pročitano`
            : `${fileName}: ništa nije pročitano — upiši ručno`}
        </span>
      )}
      {upload.error instanceof Error && (
        <span className="text-[0.74rem] text-red-700">
          {fileName}: {upload.error.message}
        </span>
      )}
      <Button
        type="button"
        variant="outline"
        size="sm"
        disabled={upload.isPending}
        onClick={() => inputRef.current?.click()}
      >
        {upload.isPending ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <FileText className="h-3.5 w-3.5" />
        )}
        Priloži projektni zadatak
      </Button>
    </span>
  );
}
